/**
 * CULT OF THE EVIL EYE - INITIATION APPLICATION
 *
 * A state-machine driven application for the token initiation flow.
 * "Can you take me higher?" - The Oath
 */

(function () {
  'use strict';

  // ============================================
  // CONFIGURATION
  // ============================================

  const CULT = window.CULT_CONFIG || {};

  const CONFIG = {
    // Supabase — public values only. The anon key is used solely to INVOKE Edge
    // Functions; it can no longer read or write any table (RLS denies it). All
    // reads/writes happen server-side inside the functions with the service_role
    // key, which never reaches the browser.
    supabaseUrl: CULT.supabaseUrl || '',
    supabaseAnonKey: CULT.supabaseAnonKey || '',
    functionsUrl: CULT.functionsUrl || ((CULT.supabaseUrl || '') + '/functions/v1'),

    // Shopify
    shopUrl: CULT.shopUrl || '',
    emporiumUrl: CULT.emporiumUrl || '/collections/all',

    // UX
    typewriterSpeed: 35,      // ms per character
    typewriterPause: 400,     // ms pause on punctuation
    transitionDuration: 400,  // ms for screen transitions
  };

  const PREFERS_REDUCED_MOTION =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Invoke a Cult Edge Function. The anon key authenticates the *invocation*;
  // the function itself enforces all authorization.
  async function callFunction(name, body, extraHeaders) {
    const res = await fetch(`${CONFIG.functionsUrl}/${name}`, {
      method: 'POST',
      headers: Object.assign({
        'Content-Type': 'application/json',
        'apikey': CONFIG.supabaseAnonKey,
        'Authorization': `Bearer ${CONFIG.supabaseAnonKey}`,
      }, extraHeaders || {}),
      body: JSON.stringify(body || {}),
    });
    let data = null;
    try { data = await res.json(); } catch (_e) { /* non-JSON */ }
    return { ok: res.ok, status: res.status, data };
  }

  // ============================================
  // CONTENT - VON GRUNDLE'S VOICE
  // ============================================

  const CONTENT = {
    // Step 1: Name
    step1: {
      prompt: 'What name do you go by in this realm?',
      placeholder: 'Speak your name...',
    },

    // Step 2: The Seeking
    step2: {
      intro: (name) => `*[The smoke stirs]* The name ${name} has weight. I have heard it before—or I will hear it again. Time is... uncertain in these matters.`,
      prompt: 'What is it you seek most?',
      options: [
        { value: 'power', label: 'Power' },
        { value: 'revenge', label: 'Revenge' },
        { value: 'redemption', label: 'Redemption' },
        { value: 'knowledge', label: 'Knowledge' },
        { value: 'protection', label: 'Protection' },
        { value: 'peace', label: 'Peace' },
        { value: 'purpose', label: 'Purpose' },
        { value: 'something-lost', label: 'Something Lost' },
      ],
    },

    seekingResponses: {
      'power': 'Power. The smoke has seen many seek this. Few wield it well. Will you?',
      'revenge': 'Revenge. *[A long pause]* The smoke knows this hunger. It has fueled seekers for millennia.',
      'redemption': 'Redemption. You carry weight from the past. The smoke does not judge—it witnesses.',
      'knowledge': 'Knowledge. The most dangerous pursuit, and the most rewarding. The smoke approves.',
      'protection': 'Protection. You have been hurt, or fear you will be. You are in the right place.',
      'peace': 'Peace. Rarer than gold. The smoke hopes you find it.',
      'purpose': 'Purpose. The empty vessel seeks to be filled. The smoke will help you find your shape.',
      'something-lost': 'Something lost. *[The smoke thickens]* The smoke sees the hole where it used to be. Perhaps it can be found.',
    },

    // Step 3: Grievance
    step3: {
      prompt: 'Has someone wronged you? A grievance that lingers?',
      subtext: 'You may speak it, or hold your peace.',
      placeholder: 'Speak your grievance, or write "none"...',
    },

    grievanceResponse: (hasGrievance) => hasGrievance
      ? 'This injustice has been recorded. The smoke does not forget.'
      : '*[A slight nod]* No grievance. The smoke respects this.',

    // Step 4: Enemy Awareness
    step4: {
      prompt: 'Do you know who your enemies are?',
      options: [
        { value: 'know-them', label: 'I know them well' },
        { value: 'suspect', label: 'I have suspicions' },
        { value: 'been-targeted', label: 'I\'ve been targeted' },
        { value: 'unsure', label: 'I am unsure' },
      ],
    },

    enemyResponses: {
      'know-them': 'You know them. You have looked into the face of casual malice. *[The eye narrows]* This is why we exist.',
      'suspect': 'You suspect. Your instincts serve you well. Trust them.',
      'been-targeted': 'You have been their target. *[The smoke darkens]* You will not face them unprotected again.',
      'unsure': 'Uncertainty is honest. The cult will help you see more clearly.',
    },

    // Step 5: Commitment
    step5: {
      prompt: 'The Eye has seen you. The smoke has heard you. Will you take the oath and join us?',
      options: [
        { value: 'yes', label: 'I will take the oath' },
        { value: 'not-ready', label: 'Not yet' },
      ],
    },

    // The Oath (inspired by "Higher" by Creed)
    oath: {
      title: 'THE OATH',
      text: `I stand before the Eye, seen and witnessed.

I seek to rise above the ordinary,
to find meaning in the mundane,
to see what others overlook.

*Can you take me higher?*
*To a place where blind men see?*

I swear to honor the bonds of this fellowship,
to guard the secrets of the smoke,
and to answer when called upon.

The Eye sees all. The Eye forgets nothing.
And now, the Eye sees *me*.`,
      affirmation: 'I speak these words freely, and bind myself to their meaning.',
    },

    // Reward Selection
    rewards: {
      intro: 'You are now a member of the Cult of the Evil Eye. As such, you are entitled to claim a token of your initiation.',
      options: [
        {
          id: 'artifact-1',
          name: 'The Watcher\'s Token',
          description: 'A small talisman bearing the Eye',
          icon: '👁️',
        },
        {
          id: 'artifact-2',
          name: 'The Smoke Keeper',
          description: 'A vessel for burning intentions',
          icon: '🌫️',
        },
        {
          id: 'artifact-3',
          name: 'The Seeker\'s Mark',
          description: 'A sigil of your new allegiance',
          icon: '⚔️',
        },
      ],
    },

    // Completion
    completion: {
      title: 'IT IS DONE',
      message: (name, rewardCode) => `Welcome to the Cult, ${name}. Your code for claiming your artifact is below.`,
      instructions: [
        'Visit the Emporium\'s secret collection',
        'Select your chosen artifact',
        'Enter your code at checkout',
        'The Eye will ensure it finds you',
      ],
    },

    // Error states
    errors: {
      invalidToken: 'This token is not recognized. The Eye does not see it.',
      expiredToken: 'This token has expired. Its time has passed.',
      claimedToken: 'This token has already been claimed. One soul per token.',
      emailUsed: 'That name has already been bound to the Eye. One soul, one initiation.',
      oathRequired: 'The oath must be sworn freely. The Eye accepts no half-measures.',
      invalidInput: 'The Eye requires more of you. Speak your name, and a way to be reached.',
      networkError: 'The smoke cannot reach the void. Try again.',
      generic: 'Something has gone wrong. The Eye is... displeased.',
    },
  };

  // ============================================
  // STATE MANAGEMENT
  // ============================================

  const STATE = {
    screen: 'loading',
    tokenCode: null,
    token: null,
    error: null,
    initiation: {
      step: 1,
      characterName: '',
      seeking: '',
      grievance: '',
      enemyAwareness: '',
      oathTaken: false,
      email: '',
      rewardChoice: '',
      rewardCode: '',
    },
  };

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  function getTokenFromUrl() {
    const params = new URLSearchParams(window.location.search);
    return params.get('token') || params.get('t');
  }

  function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  function sanitizeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function parseStageDirections(text) {
    // Convert *[stage directions]* to styled spans
    return text.replace(/\*\[(.*?)\]\*/g, '<span class="stage-direction">*[$1]*</span>');
  }

  // ============================================
  // API CLIENT (Cult Edge Functions)
  // ============================================

  const API = {
    // Validate an invitation token via the Edge Function. The server records the
    // scan and enforces the invite gate; the client is told only valid/invalid.
    async validateToken(code) {
      if (!code) return { valid: false, error: 'invalidToken' };
      try {
        const { data } = await callFunction('validate-invite', { token: code });
        if (!data || !data.valid) {
          return { valid: false, error: (data && data.reason) || 'networkError' };
        }
        return {
          valid: true,
          token: { code: String(code).toUpperCase(), tier: data.tier, faire: data.faire },
        };
      } catch (err) {
        console.error('Token validation error:', err);
        return { valid: false, error: 'networkError' };
      }
    },

    // The scan is already recorded by validate-invite; kept as a harmless no-op
    // so the initialization flow reads clearly.
    async recordScan() {
      return { success: true };
    },

    // Submit the initiation to the server, which performs the entire atomic
    // transaction (gate re-check, member creation, token claim, real reward
    // code) and returns the reward code + a session secret for the Sanctum.
    async submitInitiation(data) {
      const { data: result } = await callFunction('initiate', {
        token: STATE.tokenCode,
        characterName: data.characterName,
        email: data.email,
        seeking: data.seeking,
        grievance: data.grievance || null,
        enemyAwareness: data.enemyAwareness,
        oathTaken: data.oathTaken === true,
        rewardChoice: data.rewardChoice,
      });

      if (!result || !result.success) {
        const reason = (result && result.reason) || 'networkError';
        const err = new Error('Initiation failed: ' + reason);
        err.reason = reason;
        throw err;
      }

      // Store the session secret (Sanctum key) + display data. Authorization is
      // by session token now — never by a guessable member id.
      const session = {
        sessionToken: result.sessionToken,
        character_name: data.characterName,
        seeking: data.seeking,
        reward_code: result.rewardCode,
        created_at: new Date().toISOString(),
      };
      try { localStorage.setItem('cult_session', JSON.stringify(session)); } catch (_e) {}

      return { success: true, rewardCode: result.rewardCode };
    },
  };

  // ============================================
  // TYPEWRITER EFFECT
  // ============================================

  class Typewriter {
    constructor(element, options = {}) {
      this.element = element;
      this.speed = options.speed || CONFIG.typewriterSpeed;
      this.pauseTime = options.pauseTime || CONFIG.typewriterPause;
      this.onComplete = options.onComplete || (() => {});
      this.isComplete = false;
      this.skipRequested = false;
    }

    async type(text) {
      const parsed = parseStageDirections(text);

      const container = document.createElement('span');
      container.className = 'typewriter-text';
      this.element.innerHTML = '';
      this.element.appendChild(container);

      // Render the final, formatted HTML once, then reveal it a character at a
      // time by appending TEXT NODES (never re-parsing innerHTML per character).
      // This is O(n) text-node appends instead of O(n^2) HTML reparses, and it
      // can never leave a tag half-open mid-animation.
      const showFinal = () => { container.innerHTML = parsed; };

      // Reduced motion (or an already-requested skip): show it all instantly.
      if (PREFERS_REDUCED_MOTION) {
        showFinal();
        this.isComplete = true;
        this.element.classList.add('complete');
        this.onComplete();
        return;
      }

      const cursor = document.createElement('span');
      cursor.className = 'typewriter-cursor';
      this.element.appendChild(cursor);

      const skipBtn = document.createElement('button');
      skipBtn.className = 'typewriter-skip';
      skipBtn.type = 'button';
      skipBtn.setAttribute('aria-label', 'Skip animation');
      skipBtn.textContent = 'Skip';
      skipBtn.onclick = () => this.skip();
      this.element.appendChild(skipBtn);

      // Walk the plain-text characters; when a stage-direction span is present
      // in the formatted output we still just reveal plain text, then swap to the
      // fully-formatted HTML at the end so emphasis/stage-direction styling lands.
      const chars = this.stripHtml(parsed).split('');
      let textNode = document.createTextNode('');
      container.appendChild(textNode);

      for (let i = 0; i < chars.length; i++) {
        if (this.skipRequested) break;
        textNode.appendData(chars[i]);

        const char = chars[i];
        if ('.!?'.includes(char)) {
          await sleep(this.pauseTime);
        } else if (',;:'.includes(char)) {
          await sleep(this.pauseTime / 2);
        } else {
          await sleep(this.speed);
        }
      }

      // Swap in the formatted HTML (stage directions, emphasis) as the final state.
      showFinal();

      this.isComplete = true;
      this.element.classList.add('complete');
      skipBtn.remove();
      this.onComplete();
    }

    skip() {
      this.skipRequested = true;
    }

    stripHtml(html) {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    }
  }

  // ============================================
  // SCREEN RENDERER
  // ============================================

  const SCREENS = {
    loading() {
      return `
        <div class="screen active" id="screen-loading">
          <div class="evil-eye-container">
            <div class="evil-eye">
              <div class="eye-outer"></div>
              <div class="eye-iris"></div>
              <div class="eye-pupil"></div>
              <div class="eye-glint"></div>
            </div>
          </div>
          <p class="loading-text">The Eye awakens...</p>
        </div>
      `;
    },

    welcome() {
      return `
        <div class="screen active" id="screen-welcome">
          <div class="evil-eye-container">
            <div class="evil-eye">
              <div class="eye-outer"></div>
              <div class="eye-iris"></div>
              <div class="eye-pupil"></div>
              <div class="eye-glint"></div>
            </div>
          </div>

          <h1 class="cult-heading large">You Have Been Identified</h1>

          <div class="typewriter cult-text" id="welcome-text"></div>

          <button class="cult-button primary" id="btn-begin" style="display: none;">
            I am ready
          </button>
        </div>
      `;
    },

    step1() {
      return `
        <div class="screen active" id="screen-step1">
          <div class="progress-steps">
            <div class="progress-step active"></div>
            <div class="progress-step"></div>
            <div class="progress-step"></div>
            <div class="progress-step"></div>
            <div class="progress-step"></div>
          </div>

          <div class="evil-eye-container">
            <div class="evil-eye small">
              <div class="eye-outer"></div>
              <div class="eye-iris"></div>
              <div class="eye-pupil"></div>
              <div class="eye-glint"></div>
            </div>
          </div>

          <div class="typewriter cult-text" id="step1-text"></div>

          <div class="cult-input-group" id="step1-input" style="display: none;">
            <input
              type="text"
              class="cult-input"
              id="input-name"
              placeholder="${CONTENT.step1.placeholder}"
              maxlength="100"
              autocomplete="off"
            />
          </div>

          <button class="cult-button primary" id="btn-step1" style="display: none;">
            Continue
          </button>
        </div>
      `;
    },

    step2() {
      const optionsHtml = CONTENT.step2.options.map(opt => `
        <button class="choice-option" data-value="${opt.value}">
          ${opt.label}
        </button>
      `).join('');

      return `
        <div class="screen active" id="screen-step2">
          <div class="progress-steps">
            <div class="progress-step complete"></div>
            <div class="progress-step active"></div>
            <div class="progress-step"></div>
            <div class="progress-step"></div>
            <div class="progress-step"></div>
          </div>

          <div class="evil-eye-container">
            <div class="evil-eye small">
              <div class="eye-outer"></div>
              <div class="eye-iris"></div>
              <div class="eye-pupil"></div>
              <div class="eye-glint"></div>
            </div>
          </div>

          <div class="typewriter cult-text" id="step2-intro"></div>

          <p class="cult-text" id="step2-prompt" style="display: none;">
            ${CONTENT.step2.prompt}
          </p>

          <div class="choice-grid" id="step2-options" style="display: none;">
            ${optionsHtml}
          </div>
        </div>
      `;
    },

    step2Response() {
      return `
        <div class="screen active" id="screen-step2-response">
          <div class="evil-eye-container">
            <div class="evil-eye small">
              <div class="eye-outer"></div>
              <div class="eye-iris"></div>
              <div class="eye-pupil"></div>
              <div class="eye-glint"></div>
            </div>
          </div>

          <div class="typewriter cult-text" id="step2-response"></div>

          <button class="cult-button" id="btn-step2-continue" style="display: none;">
            Continue
          </button>
        </div>
      `;
    },

    step3() {
      return `
        <div class="screen active" id="screen-step3">
          <div class="progress-steps">
            <div class="progress-step complete"></div>
            <div class="progress-step complete"></div>
            <div class="progress-step active"></div>
            <div class="progress-step"></div>
            <div class="progress-step"></div>
          </div>

          <div class="typewriter cult-text" id="step3-text"></div>

          <p class="cult-text smoke" id="step3-subtext" style="display: none;">
            ${CONTENT.step3.subtext}
          </p>

          <div class="cult-input-group" id="step3-input" style="display: none;">
            <textarea
              class="cult-input cult-textarea"
              id="input-grievance"
              placeholder="${CONTENT.step3.placeholder}"
              maxlength="1000"
            ></textarea>
          </div>

          <button class="cult-button primary" id="btn-step3" style="display: none;">
            Continue
          </button>
        </div>
      `;
    },

    step3Response() {
      return `
        <div class="screen active" id="screen-step3-response">
          <div class="typewriter cult-text" id="step3-response"></div>

          <button class="cult-button" id="btn-step3-continue" style="display: none;">
            Continue
          </button>
        </div>
      `;
    },

    step4() {
      const optionsHtml = CONTENT.step4.options.map(opt => `
        <button class="choice-option" data-value="${opt.value}">
          ${opt.label}
        </button>
      `).join('');

      return `
        <div class="screen active" id="screen-step4">
          <div class="progress-steps">
            <div class="progress-step complete"></div>
            <div class="progress-step complete"></div>
            <div class="progress-step complete"></div>
            <div class="progress-step active"></div>
            <div class="progress-step"></div>
          </div>

          <div class="typewriter cult-text" id="step4-text"></div>

          <div class="choice-grid" id="step4-options" style="display: none;">
            ${optionsHtml}
          </div>
        </div>
      `;
    },

    step4Response() {
      return `
        <div class="screen active" id="screen-step4-response">
          <div class="typewriter cult-text" id="step4-response"></div>

          <button class="cult-button" id="btn-step4-continue" style="display: none;">
            Continue
          </button>
        </div>
      `;
    },

    step5() {
      const optionsHtml = CONTENT.step5.options.map(opt => `
        <button class="choice-option" data-value="${opt.value}">
          ${opt.label}
        </button>
      `).join('');

      return `
        <div class="screen active" id="screen-step5">
          <div class="progress-steps">
            <div class="progress-step complete"></div>
            <div class="progress-step complete"></div>
            <div class="progress-step complete"></div>
            <div class="progress-step complete"></div>
            <div class="progress-step active"></div>
          </div>

          <div class="evil-eye-container">
            <div class="evil-eye">
              <div class="eye-outer"></div>
              <div class="eye-iris"></div>
              <div class="eye-pupil"></div>
              <div class="eye-glint"></div>
            </div>
          </div>

          <div class="typewriter cult-text" id="step5-text"></div>

          <div class="choice-grid" id="step5-options" style="display: none;">
            ${optionsHtml}
          </div>
        </div>
      `;
    },

    oath() {
      return `
        <div class="screen active" id="screen-oath">
          <div class="evil-eye-container">
            <div class="evil-eye">
              <div class="eye-outer"></div>
              <div class="eye-iris"></div>
              <div class="eye-pupil"></div>
              <div class="eye-glint"></div>
            </div>
          </div>

          <h2 class="cult-heading medium">${CONTENT.oath.title}</h2>

          <div class="oath-container">
            <div class="oath-text" id="oath-text"></div>
          </div>

          <div class="oath-checkbox-group" id="oath-affirm" style="display: none;">
            <input type="checkbox" class="oath-checkbox" id="checkbox-oath" />
            <label class="oath-label" for="checkbox-oath">
              ${CONTENT.oath.affirmation}
            </label>
          </div>

          <button class="cult-button primary" id="btn-oath" style="display: none;" disabled>
            Speak the Oath
          </button>
        </div>
      `;
    },

    email() {
      return `
        <div class="screen active" id="screen-email">
          <div class="evil-eye-container">
            <div class="evil-eye small">
              <div class="eye-outer"></div>
              <div class="eye-iris"></div>
              <div class="eye-pupil"></div>
              <div class="eye-glint"></div>
            </div>
          </div>

          <div class="typewriter cult-text" id="email-text"></div>

          <div class="cult-input-group" id="email-input" style="display: none;">
            <input
              type="email"
              class="cult-input"
              id="input-email"
              placeholder="your.email@realm.com"
              autocomplete="email"
            />
            <p class="email-note">For your reward code and cult communications only.</p>
          </div>

          <button class="cult-button primary" id="btn-email" style="display: none;">
            Continue
          </button>
        </div>
      `;
    },

    reward() {
      const optionsHtml = CONTENT.rewards.options.map(opt => `
        <button class="reward-option" data-id="${opt.id}">
          <span class="reward-icon">${opt.icon}</span>
          <div class="reward-info">
            <div class="reward-name">${opt.name}</div>
            <div class="reward-desc">${opt.description}</div>
          </div>
        </button>
      `).join('');

      return `
        <div class="screen active" id="screen-reward">
          <div class="evil-eye-container">
            <div class="evil-eye small">
              <div class="eye-outer"></div>
              <div class="eye-iris"></div>
              <div class="eye-pupil"></div>
              <div class="eye-glint"></div>
            </div>
          </div>

          <div class="typewriter cult-text" id="reward-text"></div>

          <div class="reward-grid" id="reward-options" style="display: none;">
            ${optionsHtml}
          </div>

          <button class="cult-button primary" id="btn-reward" style="display: none;" disabled>
            Claim My Artifact
          </button>
        </div>
      `;
    },

    completion() {
      return `
        <div class="screen active" id="screen-completion">
          <div class="evil-eye-container">
            <div class="evil-eye">
              <div class="eye-outer"></div>
              <div class="eye-iris"></div>
              <div class="eye-pupil"></div>
              <div class="eye-glint"></div>
            </div>
          </div>

          <h1 class="cult-heading large">${CONTENT.completion.title}</h1>

          <p class="cult-text" id="completion-message"></p>

          <div class="completion-code" id="completion-code">
            <span id="reward-code-text"></span>
            <button class="cult-button ghost copy-button" id="btn-copy">Copy</button>
          </div>

          <div class="completion-instructions">
            <p class="cult-text small">To claim your artifact:</p>
            <ol>
              ${CONTENT.completion.instructions.map(i => `<li>${i}</li>`).join('')}
            </ol>
          </div>

          <div class="completion-buttons">
            <button class="cult-button primary" id="btn-sanctum">
              Enter the Inner Sanctum
            </button>
            <button class="cult-button ghost" id="btn-emporium">
              Visit the Emporium
            </button>
          </div>
        </div>
      `;
    },

    error() {
      return `
        <div class="screen screen-error active" id="screen-error">
          <div class="evil-eye-container">
            <div class="evil-eye">
              <div class="eye-outer"></div>
              <div class="eye-iris"></div>
              <div class="eye-pupil"></div>
            </div>
          </div>

          <h1 class="cult-heading large">The Eye Cannot See</h1>

          <p class="cult-text" id="error-message"></p>

          <button class="cult-button ghost" id="btn-retry">
            Try Again
          </button>
        </div>
      `;
    },

    uninvited() {
      return `
        <div class="screen active" id="screen-uninvited">
          <div class="evil-eye-container">
            <div class="evil-eye">
              <div class="eye-outer"></div>
              <div class="eye-iris"></div>
              <div class="eye-pupil"></div>
              <div class="eye-glint"></div>
            </div>
          </div>

          <div class="typewriter cult-text" id="uninvited-text"></div>

          <button class="cult-button ghost" id="btn-uninvited-shop" style="display: none;">
            Browse the Emporium
          </button>
        </div>
      `;
    },

    notReady() {
      return `
        <div class="screen active" id="screen-not-ready">
          <div class="evil-eye-container">
            <div class="evil-eye">
              <div class="eye-outer"></div>
              <div class="eye-iris"></div>
              <div class="eye-pupil"></div>
              <div class="eye-glint"></div>
            </div>
          </div>

          <div class="typewriter cult-text" id="not-ready-text"></div>

          <button class="cult-button ghost" id="btn-visit-shop" style="display: none;">
            Visit the Emporium
          </button>
        </div>
      `;
    },
  };

  // ============================================
  // APP CONTROLLER
  // ============================================

  const App = {
    container: null,

    async init() {
      this.container = document.getElementById('screen-container');

      // Get token from URL
      STATE.tokenCode = getTokenFromUrl();

      if (!STATE.tokenCode) {
        // No token at all: an organic visitor, not a forged invitation.
        // Greet them mysteriously instead of accusing them.
        this.showScreen('uninvited');
        return;
      }

      // Validate token
      try {
        const result = await API.validateToken(STATE.tokenCode);

        if (!result.valid) {
          this.showError(result.error || 'invalidToken');
          return;
        }

        STATE.token = result.token;

        // Record scan
        await API.recordScan(STATE.tokenCode);

        // Show welcome
        this.showScreen('welcome');

      } catch (err) {
        console.error('Init error:', err);
        this.showError('networkError');
      }
    },

    showScreen(screenName) {
      STATE.screen = screenName;

      // Get screen HTML
      const screenFn = SCREENS[screenName];
      if (!screenFn) {
        console.error('Unknown screen:', screenName);
        return;
      }

      // Transition out current screen
      const currentScreen = this.container.querySelector('.screen');
      if (currentScreen) {
        currentScreen.classList.add('screen-exit');
      }

      // After transition, show new screen
      setTimeout(() => {
        this.container.innerHTML = screenFn();
        this.attachHandlers(screenName);
        this.runScreenLogic(screenName);

        // Mark app as loaded
        document.getElementById('cult-app').classList.add('loaded');
        document.getElementById('screen-loading')?.remove();
      }, currentScreen ? CONFIG.transitionDuration : 0);
    },

    showError(errorType) {
      STATE.error = errorType;
      this.container.innerHTML = SCREENS.error();

      const message = CONTENT.errors[errorType] || CONTENT.errors.generic;
      document.getElementById('error-message').textContent = message;

      document.getElementById('btn-retry')?.addEventListener('click', () => {
        window.location.reload();
      });

      document.getElementById('cult-app').classList.add('loaded');
      document.getElementById('screen-loading')?.remove();
    },

    attachHandlers(screenName) {
      switch (screenName) {
        case 'welcome':
          document.getElementById('btn-begin')?.addEventListener('click', () => {
            this.showScreen('step1');
          });
          break;

        case 'step1':
          document.getElementById('btn-step1')?.addEventListener('click', () => {
            const input = document.getElementById('input-name');
            const name = input?.value?.trim();
            if (name && name.length >= 2) {
              STATE.initiation.characterName = name;
              this.showScreen('step2');
            }
          });
          document.getElementById('input-name')?.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
              document.getElementById('btn-step1')?.click();
            }
          });
          break;

        case 'step2':
          document.querySelectorAll('#step2-options .choice-option').forEach(btn => {
            btn.addEventListener('click', () => {
              STATE.initiation.seeking = btn.dataset.value;
              this.showScreen('step2Response');
            });
          });
          break;

        case 'step2Response':
          document.getElementById('btn-step2-continue')?.addEventListener('click', () => {
            this.showScreen('step3');
          });
          break;

        case 'step3':
          document.getElementById('btn-step3')?.addEventListener('click', () => {
            const input = document.getElementById('input-grievance');
            STATE.initiation.grievance = input?.value?.trim() || '';
            this.showScreen('step3Response');
          });
          break;

        case 'step3Response':
          document.getElementById('btn-step3-continue')?.addEventListener('click', () => {
            this.showScreen('step4');
          });
          break;

        case 'step4':
          document.querySelectorAll('#step4-options .choice-option').forEach(btn => {
            btn.addEventListener('click', () => {
              STATE.initiation.enemyAwareness = btn.dataset.value;
              this.showScreen('step4Response');
            });
          });
          break;

        case 'step4Response':
          document.getElementById('btn-step4-continue')?.addEventListener('click', () => {
            this.showScreen('step5');
          });
          break;

        case 'step5':
          document.querySelectorAll('#step5-options .choice-option').forEach(btn => {
            btn.addEventListener('click', () => {
              if (btn.dataset.value === 'yes') {
                this.showScreen('oath');
              } else {
                this.showScreen('notReady');
              }
            });
          });
          break;

        case 'oath':
          const checkbox = document.getElementById('checkbox-oath');
          const oathBtn = document.getElementById('btn-oath');

          checkbox?.addEventListener('change', () => {
            oathBtn.disabled = !checkbox.checked;
          });

          oathBtn?.addEventListener('click', async () => {
            oathBtn.classList.add('button-loading');
            oathBtn.disabled = true;

            STATE.initiation.oathTaken = true;
            this.showScreen('email');
          });
          break;

        case 'email':
          document.getElementById('btn-email')?.addEventListener('click', async () => {
            const input = document.getElementById('input-email');
            const email = input?.value?.trim();

            if (!email || !email.includes('@')) {
              input?.focus();
              return;
            }

            STATE.initiation.email = email;

            const btn = document.getElementById('btn-email');
            btn.classList.add('button-loading');
            btn.disabled = true;

            this.showScreen('reward');
          });

          document.getElementById('input-email')?.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
              document.getElementById('btn-email')?.click();
            }
          });
          break;

        case 'reward':
          const rewardBtn = document.getElementById('btn-reward');

          document.querySelectorAll('.reward-option').forEach(btn => {
            btn.addEventListener('click', () => {
              document.querySelectorAll('.reward-option').forEach(b => b.classList.remove('selected'));
              btn.classList.add('selected');
              STATE.initiation.rewardChoice = btn.dataset.id;
              rewardBtn.disabled = false;
            });
          });

          rewardBtn?.addEventListener('click', async () => {
            rewardBtn.classList.add('button-loading');
            rewardBtn.disabled = true;

            try {
              const result = await API.submitInitiation(STATE.initiation);
              STATE.initiation.rewardCode = result.rewardCode;
              this.showScreen('completion');
            } catch (err) {
              console.error('Submit error:', err);
              this.showError(err && err.reason ? err.reason : 'networkError');
            }
          });
          break;

        case 'completion':
          document.getElementById('btn-copy')?.addEventListener('click', () => {
            const code = STATE.initiation.rewardCode;
            navigator.clipboard.writeText(code).then(() => {
              const btn = document.getElementById('btn-copy');
              btn.textContent = 'Copied!';
              setTimeout(() => btn.textContent = 'Copy', 2000);
            });
          });

          document.getElementById('btn-sanctum')?.addEventListener('click', () => {
            window.location.href = '/pages/inner-sanctum';
          });

          document.getElementById('btn-emporium')?.addEventListener('click', () => {
            window.location.href = CONFIG.emporiumUrl;
          });
          break;

        case 'notReady':
          document.getElementById('btn-visit-shop')?.addEventListener('click', () => {
            window.location.href = CONFIG.emporiumUrl;
          });
          break;

        case 'uninvited':
          document.getElementById('btn-uninvited-shop')?.addEventListener('click', () => {
            window.location.href = CONFIG.emporiumUrl;
          });
          break;
      }
    },

    async runScreenLogic(screenName) {
      switch (screenName) {
        case 'welcome':
          const welcomeText = 'You found the token. This was not an accident. The Eye has been watching, and now... it sees *you*. *[A pause]* Are you ready to begin?';
          const tw1 = new Typewriter(document.getElementById('welcome-text'), {
            onComplete: () => {
              document.getElementById('btn-begin').style.display = '';
            }
          });
          await tw1.type(welcomeText);
          break;

        case 'step1':
          const tw2 = new Typewriter(document.getElementById('step1-text'), {
            onComplete: () => {
              document.getElementById('step1-input').style.display = '';
              document.getElementById('btn-step1').style.display = '';
              document.getElementById('input-name')?.focus();
            }
          });
          await tw2.type(CONTENT.step1.prompt);
          break;

        case 'step2':
          const introText = CONTENT.step2.intro(STATE.initiation.characterName);
          const tw3 = new Typewriter(document.getElementById('step2-intro'), {
            onComplete: () => {
              document.getElementById('step2-prompt').style.display = '';
              document.getElementById('step2-options').style.display = '';
            }
          });
          await tw3.type(introText);
          break;

        case 'step2Response':
          const response2 = CONTENT.seekingResponses[STATE.initiation.seeking];
          const tw4 = new Typewriter(document.getElementById('step2-response'), {
            onComplete: () => {
              document.getElementById('btn-step2-continue').style.display = '';
            }
          });
          await tw4.type(response2);
          break;

        case 'step3':
          const tw5 = new Typewriter(document.getElementById('step3-text'), {
            onComplete: () => {
              document.getElementById('step3-subtext').style.display = '';
              document.getElementById('step3-input').style.display = '';
              document.getElementById('btn-step3').style.display = '';
            }
          });
          await tw5.type(CONTENT.step3.prompt);
          break;

        case 'step3Response':
          const hasGrievance = STATE.initiation.grievance && STATE.initiation.grievance.toLowerCase() !== 'none';
          const response3 = CONTENT.grievanceResponse(hasGrievance);
          const tw6 = new Typewriter(document.getElementById('step3-response'), {
            onComplete: () => {
              document.getElementById('btn-step3-continue').style.display = '';
            }
          });
          await tw6.type(response3);
          break;

        case 'step4':
          const tw7 = new Typewriter(document.getElementById('step4-text'), {
            onComplete: () => {
              document.getElementById('step4-options').style.display = '';
            }
          });
          await tw7.type(CONTENT.step4.prompt);
          break;

        case 'step4Response':
          const response4 = CONTENT.enemyResponses[STATE.initiation.enemyAwareness];
          const tw8 = new Typewriter(document.getElementById('step4-response'), {
            onComplete: () => {
              document.getElementById('btn-step4-continue').style.display = '';
            }
          });
          await tw8.type(response4);
          break;

        case 'step5':
          const tw9 = new Typewriter(document.getElementById('step5-text'), {
            onComplete: () => {
              document.getElementById('step5-options').style.display = '';
            }
          });
          await tw9.type(CONTENT.step5.prompt);
          break;

        case 'oath':
          // Type out the oath with formatting
          const oathFormatted = CONTENT.oath.text.replace(/\n\n/g, '<br><br>').replace(/\n/g, '<br>');
          const oathEl = document.getElementById('oath-text');
          const tw10 = new Typewriter(oathEl, {
            speed: 40,
            onComplete: () => {
              document.getElementById('oath-affirm').style.display = '';
              document.getElementById('btn-oath').style.display = '';
            }
          });
          await tw10.type(CONTENT.oath.text);
          break;

        case 'email':
          const emailPrompt = `The oath is spoken. The Eye remembers. Now, give me a way to reach you—so that your artifact may find its way to your hands.`;
          const tw11 = new Typewriter(document.getElementById('email-text'), {
            onComplete: () => {
              document.getElementById('email-input').style.display = '';
              document.getElementById('btn-email').style.display = '';
              document.getElementById('input-email')?.focus();
            }
          });
          await tw11.type(emailPrompt);
          break;

        case 'reward':
          const tw12 = new Typewriter(document.getElementById('reward-text'), {
            onComplete: () => {
              document.getElementById('reward-options').style.display = '';
              document.getElementById('btn-reward').style.display = '';
            }
          });
          await tw12.type(CONTENT.rewards.intro);
          break;

        case 'completion':
          const msg = CONTENT.completion.message(STATE.initiation.characterName, STATE.initiation.rewardCode);
          document.getElementById('completion-message').textContent = msg;
          document.getElementById('reward-code-text').textContent = STATE.initiation.rewardCode;
          break;

        case 'notReady':
          const notReadyText = 'The invitation does not expire. The token will remember you. Return when you are ready—the Eye is patient.';
          const tw13 = new Typewriter(document.getElementById('not-ready-text'), {
            onComplete: () => {
              document.getElementById('btn-visit-shop').style.display = '';
            }
          });
          await tw13.type(notReadyText);
          break;

        case 'uninvited':
          const uninvitedText = 'The Eye regards you with... curiosity. You arrive unbidden—no token, no invitation, only nerve. *[A long pause]* Admirable. But entry to the Cult is not requested. It is *offered*. Tokens find their bearers at faires, in parcels, in places the Eye chooses. If one has not found you yet... perhaps it will. Perhaps it already watches. Until then, the Emporium is open to all.';
          const tw14 = new Typewriter(document.getElementById('uninvited-text'), {
            onComplete: () => {
              document.getElementById('btn-uninvited-shop').style.display = '';
            }
          });
          await tw14.type(uninvitedText);
          break;
      }
    },
  };

  // ============================================
  // INITIALIZE
  // ============================================

  document.addEventListener('DOMContentLoaded', () => {
    // Only run on initiation page (check for screen-container element)
    const screenContainer = document.getElementById('screen-container');
    if (!screenContainer) {
      return; // Not on initiation page, don't run this app
    }

    // Add smoke particles for atmosphere — skipped entirely for visitors who
    // prefer reduced motion (these are infinite CSS animations).
    if (!PREFERS_REDUCED_MOTION) {
      const smokeContainer = document.createElement('div');
      smokeContainer.className = 'smoke-container';
      for (let i = 0; i < 5; i++) {
        const particle = document.createElement('div');
        particle.className = 'smoke-particle';
        particle.style.left = Math.random() * 100 + '%';
        particle.style.top = Math.random() * 100 + '%';
        particle.style.animationDelay = Math.random() * 10 + 's';
        particle.style.animationDuration = (15 + Math.random() * 10) + 's';
        smokeContainer.appendChild(particle);
      }
      document.body.appendChild(smokeContainer);
    }

    // Initialize app
    App.init();
  });

})();
